import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '../../utils/convergence'
import {
  createDocuments,
  hydratePaymentOptions,
  selectPurchaseMethod,
  setCheckoutStatus,
  setSelectedPaymentType,
  updatePaymentOption,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useConsumerLendingState,
  useEffectiveRoutingDecision,
  useOfferRanks,
  usePaymentOptionsError,
  usePbiPaymentOption,
  useSelectedPaymentOption,
  useSelectedPaymentType,
  useVisiblePaymentTypes,
  useShowAffIdvModal,
  useAddPaymentMethodFlowExperimentTrigger,
  useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary,
  useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment,
  useStartedPaymentOptions,
} from '../../state'
import { CheckoutStatus, IdentityVerificationModalKey, PaymentOption, PaymentOptionsStatus } from '../../model'
import { Button, Divider, Grid, Text, useIsBreakpointLargeAndUp, useModal } from '@afterpaytouch/core'
import { PaymentType } from '@afterpay/types'
import { ConsumerWelcome } from '../PaymentTypes/ConsumerWelcome'
import { PaymentOptionsExpandableRadioGroup, usePaymentOptionsDisplayOrder } from './PaymentOptionsExpandableRadioGroup'
import { PaymentTypesTerms } from '../PaymentTypes/PaymentTypesTerms'
import { Route, useSummaryRoute } from '../../config/router'
import styles from './style.module.css'
import continueButtonStyles from './PaymentOptionsContinueButton/style.module.css'
import { TrackingEvent } from '../../model/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useSelectedPaymentOptionState } from './hooks'
import { IdentityVerificationModal } from '../IdentityVerificationModal'
import { handlePaymentOptionData } from './utils'

export const Offer: FunctionComponent = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const persistedPaymentOption = useSelectedPaymentOption()
  const selectedPaymentType = useSelectedPaymentType()
  const pbiPaymentOption = usePbiPaymentOption()
  const [lock, setLock] = useState<boolean>(false)
  const { paymentOptionsStatus, paymentOptions } = useConsumerLendingState()
  const [selectedPaymentOption, setSelectedPaymentOption] = useSelectedPaymentOptionState()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isDesktop = useIsBreakpointLargeAndUp()
  const summaryRoute = useSummaryRoute()
  const [paymentOptionsUpdated, setPaymentOptionsUpdated] = useState(false)
  const [pbiOptionSelected, setPbiOptionSelected] = useState<boolean>(false)
  const paymentMethod = useAppropriatePaymentMethod()
  const paymentOptionsError = usePaymentOptionsError()
  const shouldRoutePi4SwitcherWithNoPaymentMethodToSummary = useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary(selectedPaymentOption?.paymentType)
  const shouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment(selectedPaymentOption?.paymentType)
  const triggerAddPaymentMethodFlowExperiment = useAddPaymentMethodFlowExperimentTrigger()
  const startedPaymentOptions = useStartedPaymentOptions()
  const routingDecision = useEffectiveRoutingDecision()
  const visiblePaymentTypes = useVisiblePaymentTypes()
  const offerRanks = useOfferRanks()
  const hasTrackedOneOfferViewed = useRef(false)
  const hasTrackedPreselectionOverride = useRef(false)
  const hasTrackedOfferSelected = useRef(false)
  const showAffIdvModal = useShowAffIdvModal()
  const { showModal: showIdvModal } = useModal(IdentityVerificationModalKey)

  const { sectionOrder, sortedPclOptions } = usePaymentOptionsDisplayOrder(offerRanks)
  const firstOffer: PaymentOption | undefined = (sectionOrder[0] === PaymentType.PBI ? pbiPaymentOption : undefined) ?? sortedPclOptions[0]
  const retainedPbiOption = selectedPaymentType === PaymentType.PBI ? pbiPaymentOption : undefined
  const preselectedOptionRef = useRef<PaymentOption | undefined>(undefined)
  if (preselectedOptionRef.current === undefined) {
    preselectedOptionRef.current = persistedPaymentOption ?? retainedPbiOption ?? firstOffer
  }
  const preselectedOption = preselectedOptionRef.current
  const preselectedPaymentType = preselectedOption?.paymentType

  const preSelectedEventValue = useMemo<string>(() => {
    if (preselectedOption?.paymentType === PaymentType.PBI) {
      return 'PBI'
    }
    if (preselectedOption?.paymentType === PaymentType.PCL) {
      return `PCL-${preselectedOption?.installmentsCount}M`
    }
    return 'None'
  }, [preselectedOption])

  useEffect(() => {
    router.prefetch(Route.SUMMARY)
    router.prefetch(Route.ADD_PAYMENT_METHOD)
  }, [router])

  useEffect(() => {
    if (paymentOptionsStatus === PaymentOptionsStatus.Initial && startedPaymentOptions.length > 0) {
      dispatch(hydratePaymentOptions(startedPaymentOptions))
    }
  }, [paymentOptionsStatus, startedPaymentOptions, dispatch])

  useEffect(() => {
    const checkedOffer = selectedPaymentOption ?? preselectedOption
    if (checkedOffer === undefined) {
      return
    }
    if (selectedPaymentOption === undefined) {
      setSelectedPaymentOption(checkedOffer)
    }
    dispatch(setSelectedPaymentType(checkedOffer.paymentType))
  }, [selectedPaymentOption, preselectedOption, setSelectedPaymentOption, dispatch])

  useEffect(() => {
    if (paymentOptionsStatus === PaymentOptionsStatus.Fetched) {
      logEvent(TrackingEvent.VIEWED_CL_PAYMENT_OPTIONS_PAGE, {
        paymentOption: handlePaymentOptionData(paymentOptions),
        preSelected: preSelectedEventValue,
      })
    }
  }, [preSelectedEventValue, paymentOptionsStatus, paymentOptions])

  useEffect(() => {
    if (paymentOptionsStatus !== PaymentOptionsStatus.Fetched || hasTrackedOneOfferViewed.current) {
      return
    }
    hasTrackedOneOfferViewed.current = true
    logEvent(TrackingEvent.ONE_OFFER_PAGE_VIEWED, {
      routingDecision,
      productsShown: visiblePaymentTypes,
      productOrder: offerRanks,
      preSelected: preselectedPaymentType ?? 'None',
    })
  }, [paymentOptionsStatus, routingDecision, visiblePaymentTypes, offerRanks, preselectedPaymentType, logEvent])

  useEffect(() => {
    if (pbiOptionSelected || paymentOptionsUpdated || paymentOptionsError !== undefined) {
      if (paymentMethod !== null) {
        dispatch(selectPurchaseMethod(paymentMethod, false))
      } else {
        if (pbiOptionSelected && shouldTriggerPi4SwitcherWithNoPaymentMethodExperiment) {
          triggerAddPaymentMethodFlowExperiment({ source: 'cl_entry_pi4_switcher', selectedPaymentType: PaymentType.PBI })
          if (shouldRoutePi4SwitcherWithNoPaymentMethodToSummary) {
            router.push(summaryRoute)
            return
          }
        }
        dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
        router.push(Route.ADD_PAYMENT_METHOD)
        return
      }
      router.push(summaryRoute)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pbiOptionSelected, paymentOptionsUpdated, paymentOptionsError])

  const handleUserSelectPaymentType = (type: PaymentType): void => {
    if (hasTrackedPreselectionOverride.current || preselectedPaymentType === undefined || type === preselectedPaymentType) {
      return
    }
    hasTrackedPreselectionOverride.current = true
    logEvent(TrackingEvent.ONE_OFFER_PAGE_PRESELECTION_OVERRIDDEN, {
      preSelected: preselectedPaymentType,
      selected: type,
    })
  }

  const handleContinue = async (): Promise<void> => {
    logEvent(TrackingEvent.PRESSED_CL_ENTRY_PAGE_CONTINUE_BUTTON)
    if (!hasTrackedOfferSelected.current) {
      hasTrackedOfferSelected.current = true
      logEvent(TrackingEvent.ONE_OFFER_PAGE_OFFER_SELECTED, {
        selected: selectedPaymentOption?.paymentType,
        preSelected: preselectedPaymentType ?? 'None',
        overrodePreselection: preselectedPaymentType !== undefined && selectedPaymentOption?.paymentType !== preselectedPaymentType,
        productOrder: offerRanks,
      })
    }
    if (selectedPaymentOption === undefined) {
      return
    }
    setLock(true)
    if (selectedPaymentOption.id === pbiPaymentOption?.id) {
      if (persistedPaymentOption !== undefined && persistedPaymentOption.id !== selectedPaymentOption.id) {
        try {
          await dispatch(updatePaymentOption(selectedPaymentOption.id)).unwrap()
        } catch {
          setLock(false)
          router.push(Route.TERMINAL_ERROR)
          return
        }
      }
      if (showAffIdvModal) {
        showIdvModal()
      } else {
        setPbiOptionSelected(true)
      }
      return
    }
    logEvent(TrackingEvent.PRESSED_CL_PLAN_PAGE_CONTINUE_BUTTON, { paymentOption: selectedPaymentOption })
    try {
      await dispatch(updatePaymentOption(selectedPaymentOption.id)).unwrap()
      await dispatch(createDocuments({ autopay: true })).unwrap()
      setPaymentOptionsUpdated(true)
    } catch {
      setLock(false)
      router.push(Route.TERMINAL_ERROR)
    }
  }

  return (
    <>
      <ConsumerWelcome />
      <Divider />
      <div className={isDesktop ?? false ? styles.paymentOptionsWrapperDesktop : styles.paymentOptionsWrapperMobile}>
        <PaymentOptionsExpandableRadioGroup
          lock={lock}
          selectedPaymentOptionLocal={selectedPaymentOption}
          setSelectedPaymentOptionLocal={setSelectedPaymentOption}
          expanded
          paymentTypeOrder={offerRanks}
          onUserSelectPaymentType={handleUserSelectPaymentType}
        />
        <Grid>
          <Text color='Gray40' size='XXS' fontFace='CashSansMono' testNameSpace='one-offer-page-disclaimer'>
            {t('consumerLending:paymentOptions:oneOfferPageDisclaimer')}
          </Text>
        </Grid>
        <PaymentTypesTerms />
        <div className={continueButtonStyles.continueButton}>
          <Button
            loading={lock}
            padding='Fluid'
            onClick={handleContinue}
            testNameSpace='payment-options'
            disabled={selectedPaymentOption === undefined || lock}
          >
            {t('consumerLending:paymentOptions:continue')}
          </Button>
        </div>
        <IdentityVerificationModal setClicked={setLock} setPbiOptionSelected={setPbiOptionSelected} />
      </div>
    </>
  )
}
